import React, { useMemo, useState } from "react";
import { Offcanvas, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useTranslation } from "react-i18next";
import { Blueprint, BlueprintModule, ModuleField } from "../types";
import { ModuleCatalog } from "../useModuleCatalog";
import { findModule, moduleContext, ROUTER } from "./blueprint";
import { FieldEnv, FieldRow } from "./FieldInput";
import DataPicker from "./DataPicker";
import { moduleColor, moduleIcon } from "../moduleStyle";

interface ModulePanelProps {
    blueprint: Blueprint;
    moduleId: number | null;
    catalog: ModuleCatalog;
    samples: Record<number, any>;
    problems: Array<{ message: string; level: 'error' | 'warning' }>;
    readOnly: boolean;
    onChange: (module: BlueprintModule) => void;
    onRemove: (id: number) => void;
    onAddErrorHandler: (id: number) => void;
    onAddRoute: (id: number) => void;
    onClose: () => void;
}

/** The settings of one module, rendered from its schema. */
const ModulePanel = ({ blueprint, moduleId, catalog, samples, problems, readOnly, onChange, onRemove, onAddErrorHandler, onAddRoute, onClose }: ModulePanelProps) => {
    const { t } = useTranslation(['workflow', 'common']);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const module = moduleId !== null ? findModule(blueprint, moduleId) : undefined;
    const definition = module ? catalog.byKey[module.module] : undefined;
    const context = moduleId !== null ? moduleContext(blueprint, moduleId) : undefined;

    const env: FieldEnv = useMemo(() => ({
        upstream: (context?.ancestors || []).map((id) => {
            const key = findModule(blueprint, id)?.module || '';

            return { id, label: catalog.byKey[key]?.label || key };
        }),
        readOnly,
    }), [context, blueprint, catalog, readOnly]);

    if (!module) {
        return null;
    }

    const setParameter = (name: string, value: any) => onChange({ ...module, parameters: { ...(module.parameters || {}), [name]: value } });
    const setMapper = (name: string, value: any) => onChange({ ...module, mapper: { ...(module.mapper && typeof module.mapper === 'object' ? module.mapper : {}), [name]: value } });
    const requiredMissing = (f: ModuleField, v: any) => f.required && (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0));

    const renderFields = (fields: ModuleField[], values: Record<string, any>, set: (n: string, v: any) => void, mappable: boolean) => {
        const basic = fields.filter((f) => !f.advanced);
        const advanced = fields.filter((f) => f.advanced);

        return (
            <fieldset disabled={readOnly}>
                {basic.map((f) => (
                    <FieldRow key={f.name} field={f} value={values[f.name]} onChange={(v) => set(f.name, v)} mappable={mappable} env={env} invalid={requiredMissing(f, values[f.name])} />
                ))}
                {advanced.length > 0 &&
                    <>
                        <button type="button" className="btn btn-sm btn-ghost-primary mb-3" onClick={() => setShowAdvanced(!showAdvanced)}>
                            <i className={(showAdvanced ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line') + ' align-bottom me-1'} />
                            {showAdvanced ? t('workflow:hideAdvanced') : t('workflow:showAdvanced')}
                        </button>
                        {showAdvanced && advanced.map((f) => (
                            <FieldRow key={f.name} field={f} value={values[f.name]} onChange={(v) => set(f.name, v)} mappable={mappable} env={env} />
                        ))}
                    </>
                }
            </fieldset>
        );
    };

    const isDirective = definition?.kind === 'directive';
    const isRouter = module.module === ROUTER;
    const mapper = module.mapper && typeof module.mapper === 'object' ? module.mapper : {};

    const section = (title: string) => <h6 className="fs-13 fw-semibold text-uppercase text-muted mb-3">{title}</h6>;

    return (
        <Offcanvas isOpen={moduleId !== null} direction="end" toggle={onClose} style={{ width: 520 }} backdrop={false}>
            <div className="d-flex flex-column h-100">
                <OffcanvasHeader toggle={onClose} className="bg-light">
                    <span className="d-flex align-items-center gap-2">
                        <span className="avatar-xs flex-shrink-0">
                            <span className="avatar-title rounded-circle text-white fs-16" style={{ background: moduleColor(module.module) }}>
                                <i className={moduleIcon(definition?.icon)} />
                            </span>
                        </span>
                        <span className="mw-0">
                            <span className="d-block">{module.id}. {definition?.label || module.module}</span>
                            <span className="d-block text-muted fs-12">{module.module}</span>
                        </span>
                    </span>
                </OffcanvasHeader>
                <OffcanvasBody className="p-0 overflow-hidden">
                    <SimpleBar style={{ height: readOnly ? 'calc(100vh - 80px)' : 'calc(100vh - 140px)' }}>
                        <div className="p-4">
                            {!definition && <div className="alert alert-danger">{t('workflow:unknownModule', { key: module.module })}</div>}
                            {definition?.description && <p className="text-muted fs-14 mb-4">{definition.description}</p>}

                            {problems.map((p, i) => (
                                <div key={i} className={"alert alert-border-left " + (p.level === 'error' ? 'alert-danger' : 'alert-warning')} role="alert">
                                    <i className={(p.level === 'error' ? 'ri-error-warning-line' : 'ri-alert-line') + ' me-2 align-middle'} />{p.message}
                                </div>
                            ))}

                            {definition && definition.parameters.length > 0 &&
                                <div className="mb-2">
                                    {section(t('workflow:settings'))}
                                    {renderFields(definition.parameters, module.parameters || {}, setParameter, false)}
                                </div>
                            }

                            {definition && definition.mapper.length > 0 &&
                                <div className="mb-2">
                                    {section(t('workflow:mapping'))}
                                    {renderFields(definition.mapper, mapper, setMapper, true)}
                                    {!readOnly && <DataPicker blueprint={blueprint} moduleId={module.id} catalog={catalog} samples={samples} />}
                                </div>
                            }
                        </div>
                    </SimpleBar>
                </OffcanvasBody>
                {!readOnly &&
                    <div className="offcanvas-footer border-top p-3 position-sticky bottom-0">
                        <div className="d-flex flex-row flex-wrap gap-2">
                            {isRouter &&
                                <button type="button" className="btn btn-soft-primary flex-fill" onClick={() => onAddRoute(module.id)}>
                                    <i className="ri-git-branch-line align-bottom me-1" />{t('workflow:addRoute')}
                                </button>
                            }
                            {!isDirective && !isRouter && !module.onerror &&
                                <button type="button" className="btn btn-soft-warning flex-fill" onClick={() => onAddErrorHandler(module.id)}>
                                    <i className="ri-error-warning-line align-bottom me-1" />{t('workflow:addErrorHandler')}
                                </button>
                            }
                            <button type="button" className="btn btn-soft-danger flex-fill" onClick={() => onRemove(module.id)}>
                                <i className="ri-delete-bin-line align-bottom me-1" />{t('workflow:removeModule')}
                            </button>
                            <button type="button" className="btn btn-light flex-fill" onClick={onClose}>{t('common:close')}</button>
                        </div>
                    </div>
                }
            </div>
        </Offcanvas>
    );
};

export default ModulePanel;
